import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CLASSES } from '../core/characterUtils';
import { normTagIds } from '../core/criteriaTags';
import { useNotification, NOTIFICATION } from '../components/NotificationSystem';
import BuildListHeader from '../components/build/BuildListHeader';
import { CompactBuildCard } from '../components/build/CompactBuildCard';
import { CommunityTopCreatorsRow } from '../components/community/CommunityTopCreatorsRow';
import { DiscordCta } from '../components/community/DiscordCta';
import PageTemplate from '../components/layout/PageTemplate';
import { listCommunityBuilds, listCommunityTopCreators, toggleCommunityBuildLike } from '../services/buildApi';
import { NAV_CREATORS, navLabel } from '../utils/communityNavStrings';
import './Community.css';
import './MyBuilds.css';

const PAGE_SIZE = 48;

const TXT_LOG_IN_TO_LIKE = { fr: 'Connecte-toi pour liker.', en: 'Log in to like.', de: 'Melde dich an, um zu liken.', es: 'Inicia sesión para dar like.', pt: 'Entre para curtir.' };
const TXT_LIKE_FAILED = { fr: 'Echec du like.', en: 'Like failed.', de: 'Like fehlgeschlagen.', es: 'No se ha podido dar like.', pt: 'Não foi possível curtir.' };

const TXT = {
    title: { fr: 'Communauté', en: 'Community', de: 'Community', es: 'Comunidad', pt: 'Comunidade' },
    stuffsHeading: { fr: 'Stuffs', en: 'Stuffs', de: 'Builds', es: 'Builds', pt: 'Builds' },
    search: { fr: 'Rechercher un stuff, createur ou tag...', en: 'Search build, creator, or tag...', de: 'Build, Ersteller oder Tag suchen...', es: 'Buscar una build, creador o tag...', pt: 'Buscar uma build, criador ou tag...' },
    loading: { fr: 'Chargement...', en: 'Loading...', de: 'Wird geladen...', es: 'Cargando...', pt: 'Carregando...' },
    empty: { fr: 'Aucun résultat.', en: 'No results.', de: 'Keine Ergebnisse.', es: 'Ningún resultado.', pt: 'Nenhum resultado.' },
    results: { fr: 'résultats', en: 'results', de: 'Ergebnisse', es: 'resultados', pt: 'resultados' },
    sortLabel: { fr: 'Trier par', en: 'Sort by', de: 'Sortieren nach', es: 'Ordenar por', pt: 'Ordenar por' },
    sortLikes: { fr: 'populaires', en: 'popular', de: 'beliebt', es: 'populares', pt: 'populares' },
    sortViews: { fr: 'vues', en: 'views', de: 'Aufrufe', es: 'visitas', pt: 'visualizações' },
    sortRecent: { fr: 'récents', en: 'recent', de: 'neueste', es: 'recientes', pt: 'recentes' },
    prev: { fr: 'Précédent', en: 'Previous', de: 'Zurück', es: 'Anterior', pt: 'Anterior' },
    next: { fr: 'Suivant', en: 'Next', de: 'Weiter', es: 'Siguiente', pt: 'Seguinte' },
};

function t(dict, lang) {
    return dict[lang] || dict.en;
}

function parseCsv(raw) {
    if (!raw) return [];
    return String(raw).split(',').map(x => x.trim()).filter(Boolean);
}

function toCsv(values) {
    return values.length ? values.join(',') : '';
}

const TOP_CREATORS_FETCH_LIMIT = 50;

const Community = ({ lang = 'fr' }) => {
    const { isAuthenticated } = useAuth();
    const { showNotification } = useNotification();
    const [params, setParams] = useSearchParams();

    const [rows, setRows] = useState([]);
    const [allTagIds, setAllTagIds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [topCreators, setTopCreators] = useState([]);
    const [topCreatorsTotal, setTopCreatorsTotal] = useState(0);

    const q = params.get('q') || '';
    const sort = params.get('sort') || 'likes';
    const page = Math.max(Number(params.get('page') || 1), 1);
    const selectedClasses = useMemo(() => new Set(parseCsv(params.get('classes'))), [params]);
    const selectedTags = useMemo(() => new Set(parseCsv(params.get('tags'))), [params]);

    const [searchInput, setSearchInput] = useState(q);
    useEffect(() => setSearchInput(q), [q]);

    useEffect(() => {
        let cancelled = false;
        listCommunityTopCreators({ limit: TOP_CREATORS_FETCH_LIMIT }).then((data) => {
            if (cancelled) return;
            setTopCreators(Array.isArray(data?.results) ? data.results : []);
            setTopCreatorsTotal(Number(data?.total) || 0);
        }).catch(() => {
            if (!cancelled) {
                setTopCreators([]);
                setTopCreatorsTotal(0);
            }
        });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchInput === q) return;
            const next = new URLSearchParams(params);
            if (searchInput.trim()) next.set('q', searchInput.trim());
            else next.delete('q');
            next.set('page', '1');
            setParams(next, { replace: true });
        }, 250);
        return () => clearTimeout(timer);
    }, [searchInput, q, params, setParams]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        listCommunityBuilds({
            q,
            classes: toCsv(Array.from(selectedClasses)),
            tags: toCsv(Array.from(selectedTags)),
            sort,
            page,
            page_size: PAGE_SIZE,
        }).then((data) => {
            if (cancelled) return;
            const results = Array.isArray(data?.results) ? data.results : [];
            setRows(results);
            setTotal(Number(data?.total || 0));
            const tags = new Set();
            results.forEach((r) => normTagIds(r?.build?.criteriaTags).forEach(x => tags.add(x)));
            setAllTagIds(Array.from(tags));
        }).catch(() => {
            if (cancelled) return;
            setRows([]);
            setTotal(0);
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });
        return () => { cancelled = true; };
    }, [q, selectedClasses, selectedTags, sort, page]);

    const setCsvParam = (name, values) => {
        const next = new URLSearchParams(params);
        const serialized = toCsv(values);
        if (serialized) next.set(name, serialized);
        else next.delete(name);
        next.set('page', '1');
        setParams(next);
    };

    const toggleClass = (name) => {
        const next = new Set(selectedClasses);
        if (next.has(name)) next.delete(name);
        else next.add(name);
        setCsvParam('classes', Array.from(next));
    };

    const toggleTag = (tagId) => {
        const next = new Set(selectedTags);
        if (next.has(tagId)) next.delete(tagId);
        else next.add(tagId);
        setCsvParam('tags', Array.from(next));
    };

    const handleSort = (nextSort) => {
        const next = new URLSearchParams(params);
        next.set('sort', nextSort);
        next.set('page', '1');
        setParams(next);
    };

    const handleLike = async (id, hadLiked) => {
        if (!isAuthenticated) {
            showNotification(TXT_LOG_IN_TO_LIKE[lang] || TXT_LOG_IN_TO_LIKE.en, NOTIFICATION.INFO);
            return;
        }
        setRows(prev => prev.map(r => r.id === id ? {
            ...r,
            viewer: { ...(r.viewer || {}), hasLiked: !hadLiked },
            metrics: { ...(r.metrics || {}), likes: Math.max(0, Number(r?.metrics?.likes || 0) + (hadLiked ? -1 : 1)) },
        } : r));
        try {
            const res = await toggleCommunityBuildLike(id);
            setRows(prev => prev.map(r => r.id === id ? {
                ...r,
                viewer: { ...(r.viewer || {}), hasLiked: !!res.liked },
                metrics: { ...(r.metrics || {}), likes: Number(res.likes || 0) },
            } : r));
        } catch {
            setRows(prev => prev.map(r => r.id === id ? {
                ...r,
                viewer: { ...(r.viewer || {}), hasLiked: hadLiked },
                metrics: { ...(r.metrics || {}), likes: Math.max(0, Number(r?.metrics?.likes || 0) + (hadLiked ? 1 : -1)) },
            } : r));
            showNotification(TXT_LIKE_FAILED[lang] || TXT_LIKE_FAILED.en, NOTIFICATION.ERROR);
        }
    };

    const creatorsSectionLabel = navLabel(NAV_CREATORS, lang);
    const creatorsHref = `/${lang}/community/creators`;

    return (
        <PageTemplate header={<h1 className="page-template-title">{t(TXT.title, lang)}</h1>}>
            <div className="community-container">
                <DiscordCta lang={lang} src="community" />
                <CommunityTopCreatorsRow
                    creators={topCreators}
                    totalCreators={topCreatorsTotal}
                    lang={lang}
                    title={creatorsSectionLabel}
                    titleHref={creatorsHref}
                />
                <section className="community-builds" aria-labelledby="community-builds-heading">
                    <BuildListHeader
                        lang={lang}
                        title={<h2 id="community-builds-heading" className="community-builds-title">{t(TXT.stuffsHeading, lang)}</h2>}
                        classes={CLASSES}
                        selectedClasses={selectedClasses}
                        onToggleClass={toggleClass}
                        tagIds={allTagIds}
                        selectedTags={selectedTags}
                        onToggleTag={toggleTag}
                        searchValue={searchInput}
                        onSearchChange={setSearchInput}
                        searchPlaceholder={t(TXT.search, lang)}
                        leading={<p className="build-list-count">{total} {t(TXT.results, lang)}</p>}
                        sortValue={sort}
                        onSortChange={handleSort}
                        sortLabel={t(TXT.sortLabel, lang)}
                        sortOptions={[
                            { value: 'likes', label: t(TXT.sortLikes, lang) },
                            { value: 'views', label: t(TXT.sortViews, lang) },
                            { value: 'recent', label: t(TXT.sortRecent, lang) },
                        ]}
                    />

                    <div className="community-builds-results">
                        {loading ? (
                            <div className="mybuilds-empty"><p>{t(TXT.loading, lang)}</p></div>
                        ) : rows.length === 0 ? (
                            <div className="mybuilds-empty"><p>{t(TXT.empty, lang)}</p></div>
                        ) : (
                            <div className="community-builds-grid">
                                {rows.map((entry) => (
                                    <div key={entry.id} className="community-builds-cell">
                                        <CompactBuildCard entry={entry} lang={lang} onToggleLike={handleLike} />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {total > PAGE_SIZE && (
                        <div className="community-pagination row">
                            <button className="btn-plain" disabled={page <= 1} onClick={() => setParams(new URLSearchParams({ ...Object.fromEntries(params), page: String(page - 1) }))}>{t(TXT.prev, lang)}</button>
                            <button className="btn-plain" disabled={page * PAGE_SIZE >= total} onClick={() => setParams(new URLSearchParams({ ...Object.fromEntries(params), page: String(page + 1) }))}>{t(TXT.next, lang)}</button>
                        </div>
                    )}
                </section>
            </div>
        </PageTemplate>
    );
};

export default Community;
